import React, { useEffect, useState } from "react";
import { useAuth } from "../../common/hooks";
import BouncingDotsLoader from "../../components/DotLoading";
import { LoginStyle } from "./style";
import { localStorageItemNames } from "../../common/constants";
import { PopupMessage } from "../../common/constants/popupMessage";
import { showToast } from "../../components/Toast";
import { useLocation } from "react-router-dom";
import axios from "axios";

/* ── Inline SVG icons (no extra dependency) ── */
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const Login = () => {
  const classes = LoginStyle();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userNameError, setUserNameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const { onLoggedIn, loading, setLoggedIn, setUser } = useAuth();
  const { state } = useLocation();

  const reloadMessage = localStorage.getItem(
    localStorageItemNames.showMessageAfterReload
  );

  const login = async () => {
    setUserNameError(false);
    setPasswordError(false);

    if (!userName?.trim()) {
      setUserNameError(true);
      // showToast("Please enter username", "error");
      return;
    }

    if (!password?.trim()) {
      setPasswordError(true);
      // showToast("Please enter password", "error");
      return;
    }

    await onLoggedIn({ userName, password, email: "" });
  };

  useEffect(() => {
    setLoggedIn(false);
    setUser(null);

    if (reloadMessage) {
      localStorage.removeItem(localStorageItemNames.showMessageAfterReload);
    }

    if (state?.data === "userNewVersion") {
      window.history.replaceState({}, "");
      showToast(PopupMessage.loginAgain, "info");
      const t = setTimeout(() => window.location.reload(), 2000);
      return () => clearTimeout(t);
    }

    if (state?.data === "userDeativated") {
      window.history.replaceState({}, "");
      showToast(PopupMessage.userDeactivated, "info");
      const t = setTimeout(() => window.location.reload(), 2000);
      return () => clearTimeout(t);
    }

    if (state?.data === "logoutUser") {
      window.history.replaceState({}, "");
      const t = setTimeout(() => window.location.reload(), 1);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className={classes.loginContainer}>
      {/* Decorative layers */}
      <div className={classes.backgroundBlob1} />
      <div className={classes.backgroundBlob2} />
      <div className={classes.particleLayer} />

      {/* ── LEFT PANEL ── */}
      <div className={classes.leftPanel}>
        <div>
          <div className={classes.brand}>
            <span className={classes.brandDivider} />
            Vehicle Data Analysis Tool Platform
          </div>

          <h1 className={classes.heading}>
            One Platform.
            <br />
            Endless <span>Possibilities.</span>
          </h1>

          <p className={classes.description}>
            Streamline operations, make data-driven decisions and accelerate
            growth with advanced fleet analytics.
          </p>

          <div className={classes.featureGrid}>
            <div className={classes.featureCard}>
              <div className={classes.featureIcon}>📊</div>
              <div>
                <h4>Insightful Analytics</h4>
                <span>Real-time dashboards and reports</span>
              </div>
            </div>

            <div className={classes.featureCard}>
              <div className={classes.featureIcon}>🔒</div>
              <div>
                <h4>Secure &amp; Reliable</h4>
                <span>Enterprise-grade security</span>
              </div>
            </div>

            <div className={classes.featureCard}>
              <div className={classes.featureIcon}>🔧</div>
              <div>
                <h4>Seamless Integration</h4>
                <span>Connect all your systems</span>
              </div>
            </div>

            <div className={classes.featureCard}>
              <div className={classes.featureIcon}>🚘</div>
              <div>
                <h4>Fleet Performance</h4>
                <span>Track vehicle efficiency</span>
              </div>
            </div>
          </div>

          <div className={classes.stats}>
            <div>
              <h3>10M+</h3>
              <span>Vehicle Records</span>
            </div>
            <div>
              <h3>250+</h3>
              <span>Enterprise Clients</span>
            </div>
            <div>
              <h3>99.99%</h3>
              <span>Platform Uptime</span>
            </div>
          </div>
        </div>

        <div className={classes.footer}>
          © 2026 Vehicle Data Analysis Tool Platform. All rights reserved.
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className={classes.rightPanel}>
        <div className={classes.loginCard}>

          {/* Logo */}
          <div className={classes.logoWrap}>
            <div style={{
              // background: "#fafafa",
              borderRadius: "16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "fit-content"
            }}>
              <img src="/logo.png" alt="Logo" style={{ height: "120px", width: "auto", objectFit: "contain" }} />
            </div>
          </div>

          {/* Titles */}
          <h2 className={classes.welcomeTitle}>Welcome Back</h2>
          <p className={classes.subtitle}>
            Sign in to continue to your analytics dashboard
          </p>

          {/* Username */}
          <label className={classes.fieldLabel}>Username</label>
          <div className={classes.inputWrap}>
            <span className={classes.inputIcon}><UserIcon /></span>
            <input
              id="login-username"
              type="text"
              required
              autoComplete="off"
              placeholder="Enter your username"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
                if (e.target.value.trim()) setUserNameError(false);
              }}
              className={classes.input}
              style={
                userNameError
                  ? { borderColor: "#EF4444", boxShadow: "0 0 0 4px rgba(239,68,68,.18)" }
                  : {}
              }
              onKeyDown={(e) => e.key === "Enter" && login()}
            />
          </div>
          {userNameError && (
            <div className={classes.inlineError}>Username is required</div>
          )}

          {/* Password */}
          <label className={classes.fieldLabel}>Password</label>
          <div className={classes.inputWrap}>
            <span className={classes.inputIcon}><LockIcon /></span>
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="off"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (e.target.value.trim()) setPasswordError(false);
              }}
              className={classes.input}
              style={
                passwordError
                  ? { borderColor: "#EF4444", boxShadow: "0 0 0 4px rgba(239,68,68,.18)" }
                  : {}
              }
              onKeyDown={(e) => e.key === "Enter" && login()}
            />
            <button
              type="button"
              className={classes.eyeIcon}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          {passwordError && (
            <div className={classes.inlineError}>Password is required</div>
          )}

          {/* Remember / Forgot */}
          <div className={classes.rememberRow}>
            <label>
              <input type="checkbox" id="remember-me" />
              Remember me
            </label>
          </div>

          {/* Sign In */}
          <button
            id="sign-in-btn"
            type="button"
            className={classes.loginButton}
            onClick={login}
            disabled={loading}
          >
            {loading ? <BouncingDotsLoader /> : <>Sign In &nbsp;→</>}
          </button>
        </div>
      </div>
    </div>
  );
};
